import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { brokerService, getErrorMessage } from '@/services';
import { targetService } from '@/services/targets';
import type {
  AchievementQuery,
  AchievementResponse,
  BulkOptions,
  BulkTargetRow,
} from '@/services/targets';
import type { POSP, Target, TargetFilters, TargetPeriodType } from '@/types';

/** Board #263 — monthly premium targets. */
export function useTargets(filters?: TargetFilters, options?: { enabled?: boolean }) {
  return useQuery<Target[]>({
    queryKey: ['targets', filters ?? {}],
    queryFn: () => targetService.listTargets(filters),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Every active POSP of the caller's broker, for the targets grid.
 *
 * `usePOSPsLookup` returns page 1 only (20 rows) — right for a dropdown, wrong
 * for a grid: a broker's 21st POSP would silently have no row and no way to be
 * given a target. This follows `next` to the end, like `listTargets` does.
 */
export function useTargetPOSPs() {
  return useQuery<POSP[]>({
    queryKey: ['posps', 'targets-grid', 'active'],
    queryFn: async () => {
      const all: POSP[] = [];
      for (let page = 1; page <= 100; page += 1) {
        const result = await brokerService.getPOSPs(page, { status: 'active' });
        all.push(...(result.results ?? []));
        if (!result.next) break;
      }
      return all;
    },
    staleTime: 60 * 1000,
  });
}

export function useAchievement(query?: AchievementQuery, options?: { enabled?: boolean }) {
  return useQuery<AchievementResponse>({
    queryKey: ['target-achievement', query ?? {}],
    queryFn: () => targetService.getAchievement(query),
    enabled: options?.enabled ?? true,
  });
}

export function useBulkUpsertTargets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: BulkTargetRow[] | { rows: BulkTargetRow[]; options?: BulkOptions }) =>
      Array.isArray(vars)
        ? targetService.bulkUpsert(vars)
        : targetService.bulkUpsert(vars.rows, vars.options),
    onSuccess: (saved) => {
      // Refetch rather than patch the cache: the grid must show what the
      // server actually stored, not what we hoped it stored.
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
      void queryClient.invalidateQueries({ queryKey: ['target-achievement'] });
      toast.success(
        saved.length === 1 ? '1 target saved' : `${saved.length} targets saved`
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** #343 -- the logged-in POSP's own targets for one period (server self-scopes). */
export function useMyTargets(periodType: TargetPeriodType) {
  return useQuery({
    queryKey: ['my-targets', periodType],
    queryFn: () => targetService.getMine({ period_type: periodType }),
  });
}

/** #343 -- closed periods with incentive status. */
export function useMyTargetHistory(periodType: TargetPeriodType) {
  return useQuery({
    queryKey: ['my-targets-history', periodType],
    queryFn: () => targetService.getMineHistory(periodType),
  });
}
